// src/components/EventsDisplayArea.js
import React, { useState } from 'react';
import { 
  Clock, MessageSquare, X, AlertCircle, ChevronDown, ChevronRight,
  Search, Filter, Video, User, Car, Calendar, Bot, ArrowDown, ArrowUp,
  ExternalLink
} from "lucide-react";
import { useSharedEvents } from '../TIMEBAR/SharedEventsContext';
import { useAppStore } from '../../store/appStore';
import './EventsDisplayArea.css';

const EventsDisplayArea = ({ source = 'workspace' }) => {  // Add source prop
  const [expandedEvents, setExpandedEvents] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState('desc');
  const [robotFilter, setRobotFilter] = useState(null);
  
  // Get shared events from context
  const { sharedEvents, removeSharedEvent } = useSharedEvents();
  
  // Get the updateVideoState function from app store
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // Items per page
  const ITEMS_PER_PAGE = 5;
  
  // Filter events based on criteria and source
  const filteredEvents = sharedEvents.filter(event => {
    // Filter by source (workspace/shared)
    if (!Array.isArray(event.savedTo)) {
      event.savedTo = [event.savedTo || 'workspace'];
    }
    if (!event.savedTo.includes(source)) {
      return false;
    }
    
    // Filter by active filter type
    if (activeFilter !== 'all') {
      if (activeFilter === 'video') {
        if (event.source && event.source !== 'video') {
          return false;
        }
      } else if (event.source !== activeFilter) {
        return false;
      }
    }
    
    // Filter by robot ID
    if (robotFilter && event.robotId !== robotFilter) {
      return false;
    }
    
    return true;
  });
  
  // Sort events
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const getTimestamp = (event) => {
      if (event.savedAt) return new Date(event.savedAt).getTime();
      if (event.timestamp) return new Date(event.timestamp).getTime();
      return event.startTime || 0;
    };
    
    const aTime = getTimestamp(a);
    const bTime = getTimestamp(b);
    
    return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
  });
  
  // Paginate
  const paginatedEvents = sortedEvents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Total pages
  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
  
  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };
  
  // Toggle event expand
  const toggleEventExpand = (eventId, robotId) => {
    const key = `${robotId || ''}-${eventId}`;
    setExpandedEvents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Handle remove event
  const handleRemoveEvent = (id, robotId) => {
    removeSharedEvent(id, robotId);
  };
  
  // Set robot filter
  const handleRobotFilter = (robotId) => {
    setRobotFilter(prev => prev === robotId ? null : robotId);
    setCurrentPage(1);
  };
  
  // Format time
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };
  
  // Create timestamp
  const createTimeStamp = (timeframe, startTime) => {
    const isDaytime = timeframe === "11AM";
    const minutes = Math.floor(startTime / 60);
    const seconds = Math.floor(startTime % 60);
    let hour = isDaytime ? 11 : 23;
    let finalMinutes = minutes;
    
    hour += Math.floor(finalMinutes / 60);
    finalMinutes = finalMinutes % 60;
    
    const formattedHour = hour > 12 ? `${hour - 12}` : `${hour}`;
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedMinutes = finalMinutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    
    return `${formattedHour}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
  };
  
  // Get source icon
  const getSourceIcon = (source) => {
    switch(source) {
      case 'video': return <AlertCircle size={16} />;
      case 'person': return <User size={16} />;
      case 'vehicle': return <Car size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };
  
  // Get event icon
  const getEventIcon = (type, source) => {
    if (source && source !== 'video') {
      return getSourceIcon(source);
    }
    
    if (!type) return <AlertCircle size={18} />;
    
    switch(Number(type)) {
      case 1: return <img src="/smallicons/important_car_small.png" alt="Civil-Vehicle" width="18" height="18" />;
      case 2: return <img src="/smallicons/important_people_small.png" alt="Civil-Person" width="18" height="18" />;
      case 3: return <img src="/smallicons/important_others_small.png" alt="Civil-Other" width="18" height="18" />;
      case 4: return <img src="/smallicons/urgent_car_small.png" alt="Crime-Vehicle" width="18" height="18" />;
      case 5: return <img src="/smallicons/urgent_people_small.png" alt="Crime-Person" width="18" height="18" />;
      case 6: return <img src="/smallicons/urgent_others_small.png" alt="Crime-Other" width="18" height="18" />;
      default: return <AlertCircle size={18} />;
    }
  };
  
  // Get priority color
  const getPriorityColor = (priority) => {
    const priorityNum = Number(priority);
    switch(priorityNum) {
      case 1: return "#bd0026"; // Emergency
      case 2: return "#f03b20"; // Urgent
      case 3: return "#fd8d3c"; // Moderate
      case 4: return "#90EE90"; // Advisory
      default: return "#9CA3AF";
    }
  };
  
  // Get priority label
  const getPriorityLabel = (priority) => {
    const priorityNum = Number(priority);
    switch(priorityNum) {
      case 1: return "Emergency";
      case 2: return "Urgent";
      case 3: return "Moderate";
      case 4: return "Advisory";
      default: return "Unclassified";
    }
  };
  
  // Get event source
  const getEventSource = (event) => {
    return event.source || 'video';
  };
  
  // Handler for "Check Details" button
  const handleGoDetailsClick = (event, e) => {
    e.stopPropagation(); // Prevent the card click event from triggering
    
    // Generate timePart based on timeframe
    const timePart = event.timeframe === "11AM" ? "day" : "night";
    const videoUrl = `/${timePart}/videos/video${event.robotId}.mp4`;
    
    console.log("Go Details clicked for event:", event);
    
    // Update the video state
    try {
      updateVideoState({
        robotId: event.robotId,
        eventId: null, // We don't have an event index here
        videoUrl,
        time: event.startTime,
        isPlaying: true,
        isHls: false,
        hlsResumeTime: null,
        hlsBaseTime: 0,
        currentEvent: event
      }, 'events_display_go_details');
      
      console.log(`Go Details: Video state updated, jumping to time: ${event.startTime}s`);
    } catch (err) {
      console.error(`Go Details: Error updating video state:`, err);
    }
  };
  
  return (
    <div className="events-display-area">
      <div className="events-controls">
        <div className="events-filters">
          <button 
            className={`events-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            <Filter size={14} />
            All
          </button>
          <button 
            className={`events-filter-btn ${activeFilter === 'video' ? 'active' : ''}`}
            onClick={() => setActiveFilter('video')}
          >
            <Video size={14} />
            Video
          </button>
          <button 
            className={`events-filter-btn ${activeFilter === 'person' ? 'active' : ''}`}
            onClick={() => setActiveFilter('person')}
          >
            <User size={14} />
            Person
          </button>
          <button 
            className={`events-filter-btn ${activeFilter === 'vehicle' ? 'active' : ''}`}
            onClick={() => setActiveFilter('vehicle')}
          >
            <Car size={14} />
            Vehicle
          </button>
        </div>
      </div>

      <div className="events-content">
        {paginatedEvents.length === 0 ? (
          <div className="events-empty">
            {searchTerm || activeFilter !== 'all' || robotFilter ? (
              <p>No matching items found. Try adjusting your filters.</p>
            ) : (
              <>
                <p>No events in {source === 'workspace' ? 'saved space' : 'shared space'} yet</p>
                <p className="events-empty-hint">Add events with notes ... </p>
              </>
            )}
          </div>
        ) : (
          <div className="events-list">
            {paginatedEvents.map((event) => {
              const eventKey = `${event.robotId || ''}-${event.id}`;
              const isExpanded = expandedEvents[eventKey];
              const hasNotes = event.notes && event.notes.length > 0;
              const eventSource = getEventSource(event);
              
              // Generate timestamp
              let timestamp = "";
              if (event.timeframe && event.startTime !== undefined) {
                timestamp = createTimeStamp(event.timeframe, event.startTime);
              } else if (event.timestamp) {
                timestamp = event.timestamp;
              } else if (event.videoInfo) {
                timestamp = event.videoInfo;
              } else {
                timestamp = formatTime(event.startTime);
              }
              
              return (
                <div 
                  key={eventKey}
                  className="shared-event-card"
                  style={{
                    borderLeft: `4px solid ${getPriorityColor(event.priority || 3)}`
                  }}
                >
                  <div className="shared-event-header">
                    <div className="shared-event-title-area">
                      <div className="shared-event-icon">
                        {getEventIcon(event.type, eventSource)}
                      </div>
                      
                      <div className="shared-event-info">
                        <div className="shared-event-meta">
                          <span 
                            className="shared-event-priority"
                            style={{ 
                              backgroundColor: getPriorityColor(event.priority || 3)
                            }}
                          >
                            {getPriorityLabel(event.priority || 3)}
                          </span>
                          <span className="shared-event-time">
                            <Clock size={12} />
                            {timestamp}
                          </span>
                          {event.savedAt && (
                            <span className="shared-event-save-time">
                              <Calendar size={12} />
                              Saved: {new Date(event.savedAt).toLocaleString()}
                            </span>
                          )}
                          {hasNotes && (
                            <span className="shared-event-notes-count">
                              <MessageSquare size={12} />
                              {event.notes.length}
                            </span>
                          )}
                          
                          <span className={`shared-event-source shared-event-source-${eventSource}`}>
                            {getSourceIcon(eventSource)}
                            <span>{eventSource === 'video' ? 'Event' : eventSource.charAt(0).toUpperCase() + eventSource.slice(1)}</span>
                          </span>
                          
                          {event.robotId && (
                            <span className="shared-event-robot">
                              <Bot size={12} />
                              <span>Robot {event.robotId}</span>
                            </span>
                          )}
                        </div>
                        
                        <h3 className="shared-event-title">
                          {event.event || "Unnamed Event"}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="shared-event-actions">
                      <button 
                        className="shared-event-toggle"
                        onClick={() => toggleEventExpand(event.id, event.robotId)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                      
                      <button 
                        className="shared-event-remove"
                        onClick={() => handleRemoveEvent(event.id, event.robotId)}
                        aria-label="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="shared-event-content">
                      {/* Left column - Image */}
                      <div className="shared-event-image-column">
                        <div className="shared-event-image-container">
                          <img 
                            src={event.imagePath || `/api/placeholder/300/200?text=${eventSource}`}
                            className="shared-event-image"
                            alt={event.event || "Event"}
                            onError={(e) => {
                              e.target.src = `/api/placeholder/300/200?text=${eventSource}`;
                            }}
                          />
                        </div>
                        
                        {/* Add Check Details button */}
                        <div className="event-check-details-container">
                          <button 
                            className="event-check-details-btn"
                            onClick={(e) => handleGoDetailsClick(event, e)}
                            title="Go to video and map view"
                          >
                            <ExternalLink size={14} />
                            <span>Check Details</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Right column - Details */}
                      <div className="shared-event-details-column">
                        {/* Display descriptors (if from person or vehicle) */}
                        {event.descriptors && event.descriptors.length > 0 && (
                          <div className="shared-event-descriptors">
                            <h4 className="shared-event-descriptors-title">Details:</h4>
                            <div className="shared-event-descriptors-text">
                              {event.descriptors.join(', ')}
                            </div>
                          </div>
                        )}
                        
                        {/* Display event description */}
                        {event.summary && (
                          <div className="shared-event-summary">
                            <p>{event.summary}</p>
                          </div>
                        )}
                        
                        {/* Display notes list */}
                        {hasNotes && (
                          <div className="shared-event-notes">
                            <h4 className="shared-event-notes-title">Notes:</h4>
                            <div className="shared-event-notes-list">
                              {event.notes.map(note => {
                                // Format time
                                const noteDate = new Date(note.timestamp);
                                const formattedDate = `${noteDate.toLocaleDateString()} ${noteDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
                                
                                return (
                                  <div 
                                    key={note.id}
                                    className={`shared-note ${note.isImportant ? 'important' : ''}`}
                                  >
                                    <div className="shared-note-header">
                                      <div className="shared-note-timestamp">
                                        <Clock size={12} />
                                        <span>{formattedDate}</span>
                                      </div>
                                      {note.isImportant && (
                                        <div className="shared-note-important-tag">
                                          <AlertCircle size={12} />
                                          <span>Important</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="shared-note-content">
                                      {note.content}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="events-pagination">
            <button 
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsDisplayArea;